import type { JSX } from 'react';
import { useState } from 'react';
import { Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { IMPORT_PARTS, type ImportPart } from '@pst/shared';
import { ApiError, type Project } from '../api';
import { availableParts, downloadJson, exportProject, withNotes } from './transfer';

/**
 * Exporting a project as a PST-Import file - all of it, or only the parts
 * chosen. A file with only "Kostenplanung", for instance, can be imported
 * into another project without touching its schedule.
 */
export function ExportModal({ project, active, onClose, onError }: {
  project: Project;
  active: ReadonlySet<string>;
  onClose: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const available = availableParts(active);
  const [chosen, setChosen] = useState<Set<ImportPart>>(new Set(available));
  const [entries, setEntries] = useState(false);
  const [notes, setNotes] = useState(true);
  const [busy, setBusy] = useState(false);
  const toggle = (part: ImportPart, on: boolean): void => setChosen((s) => { const n = new Set(s); if (on) n.add(part); else n.delete(part); return n; });
  const suffix = chosen.size === available.length ? '' : `-${[...chosen].join('-')}`;

  const run = (): void => {
    setBusy(true);
    const schema = notes ? fetch('/api/docs/pst-import-v1.schema.json').then((r) => (r.ok ? (r.json() as Promise<object>) : null)).catch(() => null) : Promise.resolve(null);
    exportProject(project, active, chosen, { timeEntries: entries })
      .then(async (doc) => { downloadJson(notes ? withNotes(doc, await schema) : doc, `${project.number}${suffix}-pst-import.json`); onClose(); })
      .catch((e: unknown) => { onError(e instanceof ApiError ? e.message : 'Export fehlgeschlagen.'); onClose(); })
      .finally(() => setBusy(false));
  };

  return (
    <Modal opened onClose={onClose} title={`${project.number} exportieren`}>
      <Stack gap="sm">
        <Text size="sm">Eine PST-Import-Datei (JSON). Nummer und Name des Projekts stehen immer darin, dazu die gewählten Teile.</Text>
        {IMPORT_PARTS.filter((p) => available.includes(p.key)).map((p) => (
          <Checkbox key={p.key} label={p.label} checked={chosen.has(p.key)} onChange={(e) => toggle(p.key, e.currentTarget.checked)} />
        ))}
        {chosen.has('time') && (
          <Checkbox
            ml="lg" size="xs" checked={entries} onChange={(e) => setEntries(e.currentTarget.checked)}
            label="mit den gebuchten Zeiten – personenbezogen, mit Namen; nur die, die ich sehen darf"
          />
        )}
        <Checkbox
          checked={notes} onChange={(e) => setNotes(e.currentTarget.checked)}
          label="mit Hinweisen zum Aufbau" description="Regeln und die Grenze jedes Felds (z. B. Länge von Namen) am Anfang der Datei – damit Claude Chat beim Bearbeiten gültige Dateien liefert. Der Import ignoriert sie."
        />
        <Group justify="space-between">
          <Group gap={4}>
            <Button size="xs" variant="subtle" onClick={() => setChosen(new Set(available))}>alle</Button>
            <Button size="xs" variant="subtle" onClick={() => setChosen(new Set())}>keine</Button>
          </Group>
          <Group gap="xs">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button onClick={run} loading={busy} disabled={chosen.size === 0}>Herunterladen</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
